<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import AppLoader from '../../components/AppLoader.vue';
import PageBreadcrumb from '../../components/PageBreadcrumb.vue';
import AppFooter from '@/components/AppFooter.vue';
import ConnectorTab from '../../components/ConnectorTab.vue';
import ErrorMessage from '../../components/ErrorMessage.vue';

import { useTransformsStore } from '../../stores/transforms.store';
import { useRouter, useRoute, onBeforeRouteLeave } from 'vue-router';
import { useSidebar } from '@/components/ui/sidebar';
import { useUrlFilters } from '@/composables/useUrlFilters';
import { FILTER_DEFAULTS } from '@/utils/constants';
import _ from 'lodash';
import ActionMenu from '@/components/ActionMenu.vue';
import StatCard from '@/components/global/StatCard.vue';
import { SkeletonLoader, FormSkeleton } from '@/components/ui/skeleton-loader';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableEmpty } from '@/components/ui/table';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';

import { ChevronRight, CheckCircle, Cpu, Clock, List, Sliders } from 'lucide-vue-next';

import { useDeleteConfirmation } from '@/composables/useDialog';
import TransformImplementationPage from './TransformImplementationPage.vue';
import { toast } from '@/composables/useToast';
import { useAuthStore } from '@/stores/auth.store';
import { userHasAccess } from '@/utils/utils';

import { formatter, getMetricFormattingInfo, getStatusColor } from '../../utils/utils';
import Tabs from '@/components/global/Tabs.vue';
import { getTransformIcon } from '@/utils/constants';
import { useTopicsStore } from '@/stores/topics.store';
import { TableEmptyState } from '@/components/ui/table-empty-state';
import TablePagination from '@/components/TablePagination.vue';
import { fetchTopics as fetchTopicsApi } from '@/api/topics.api';
import { fetchMetricsForTopicsTable } from '@/api/metrics.api';
const props = defineProps(['id', 'customMessage', 'source', 'destination']);
const transformsStore = useTransformsStore();
const topicsStore = useTopicsStore();
const authStore = useAuthStore();

let transform = ref(null);
let router = useRouter();
let route = useRoute();

let generalLoading = ref(false);
let actionLoading = ref(false);
let configurationLoading = ref(false);
let tabLoading = ref(false);
const { isMobile, setOpen, setOpenMobile } = useSidebar();

// URL filter management
const { registerFilter } = useUrlFilters({
    defaults: {
        tab: FILTER_DEFAULTS.TABS.STATUS
    }
});

const currentTab = registerFilter('tab', FILTER_DEFAULTS.TABS.STATUS);

const chart = ref(null);
const hidePromptTemporary = ref(false);
let hideTabs = ref(false);

let canSave = ref(false);

const selectedChart = ref('throughput');
const chartVariants = ref([
    {
        id: 'throughput',
        metricName: 'Throughput',
        chartName: 'Throughput Over Time',
        unit: 'GB',
        data: [
            150.0, 200.5, 250.4, 300.2, 350.0, 400.0, 450.6, 500.5, 550.4, 600.1, 650.6, 700.4, 550.0, 800.5, 250.4, 300.2, 350.0, 400.0, 450.6,
            500.5, 550.4, 600.1, 650.6, 700.4
        ], // Mock data
        color: '#958cee'
    },
    {
        id: 'lag',
        metricName: 'Lag',
        chartName: 'Lag Over Time',
        unit: 's',
        data: [0.1, 0.5, 0.4, 0.2, 0.0, 0.0, 0.6, 0.5, 0.4, 0.1, 0.6, 0.4, 0.0, 0.5, 0.4, 0.2, 0.0, 0.0, 0.6, 0.5, 0.4, 0.1, 0.6, 0.4], // Mock data
        color: '#ee8c8c'
    },
    {
        id: 'latency',
        metricName: 'Latency',
        chartName: 'Latency Over Time',
        unit: 'ms',
        data: [120, 85, 600, 300, 150, 200, 250, 400, 450, 500, 550, 600, 650, 700, 550, 800, 250, 300, 350, 400, 450, 500, 550, 600], // Mock data
        color: '#8cadee'
    }
]);

const chartOptions = ref(null);

let metricsLoading = ref(false);
let defaultTransform = ref('');

let tabs = [
    { name: 'Status', href: '#', current: true },
    { name: 'Settings', href: '#', current: false },
    { name: 'Implementation', href: '#', current: true }
];

const setCurrentTab = (str) => {
    if (str === 'Source' || str === 'Destination') {
        return;
    }

    currentTab.value = str;
    // URL updated automatically by useUrlFilters composable
};

const transformConfig = computed(() => {
    if (transformsStore.configs) {
        const found = Object.values(transformsStore.configs).find(
            (x) => x.filter((y) => (y.transform || y.transform_type) === (transform.value.transform || transform.value.transform_type)).length
        );

        return found?.[0]?.config;
    }
});

const transformTypeDisplayName = computed(() => {
    if (transformsStore.configs) {
        const found = Object.values(transformsStore.configs).find(
            (x) => x.filter((y) => (y.transform || y.transform_type) === (transform.value.transform || transform.value.transform_type)).length
        );

        return found?.[0]?.display_name;
    }
});

// Watch for tab changes to handle sidebar and UI state
watch(
    () => currentTab.value,
    (newTab) => {
        if (newTab === 'Implementation') {
            // Collapse sidebar for Implementation tab
            if (isMobile.value) {
                setOpenMobile(false);
            } else {
                setOpen(false);
            }
            hideTabs.value = true;
        } else {
            // Expand sidebar for other tabs
            if (isMobile.value) {
                setOpenMobile(true);
            } else {
                setOpen(true);
            }
            hideTabs.value = false;
        }
    },
    { immediate: true }
);

const getFormattedValue = (metricKey, value) => {
    const found = Object.values(transformsStore.configs).find(
        (x) => x.filter((y) => (y.transform || y.transform_type) === (transform.value.transform || transform.value.transform_type)).length
    );

    const metricInfo = getMetricFormattingInfo(metricKey, found?.[0]?.metrics);

    return formatter(value, metricInfo || {});
};

const stats = computed(() => {
    return [
        {
            id: 0,
            name: 'Status',
            stat: getFormattedValue('status', transform.value?.metrics?.status).display,
            icon: Sliders,
            colorClass: getStatusColor('active', false, true)
        },
        {
            id: 2,
            name: 'Tasks',
            stat: getFormattedValue('tasks', transform.value?.metrics?.tasks).display,
            icon: List,
            colorClass: 'bg-primary'
        },
        {
            id: 4,
            name: 'Latency',
            icon: Clock,
            colorClass: 'bg-primary/50',
            stat: getFormattedValue('latency', transform.value?.metrics?.latency).display
        },
        {
            id: 4,
            name: 'Duration',
            stat: getFormattedValue('duration', transform.value?.metrics?.duration).display,
            icon: Cpu,
            colorClass: 'bg-chart-4'
        }
    ];
});
// topics table: fetch and manage topics for this transform separately
const transformTopics = ref([]);
const transformTopicsPage = ref(0);
const transformTopicsPageSize = ref(10);
const transformTopicsTotal = ref(0);
const transformTopicsLoading = ref(false);
const transformTopicsMetricsLoading = ref(false);

async function fetchTransformTopics(pageNumber = null, newPageSize = null) {
    transformTopicsLoading.value = true;
    transformTopicsMetricsLoading.value = true;
    try {
        const allTopicIds = transform.value?.topic_ids || [transform.value?.topic_id] || [];

        if (!allTopicIds?.length) {
            transformTopics.value = [];
            transformTopicsTotal.value = 0;
            return;
        }

        const topicIdPrefix = allTopicIds[0]?.match(/(transform_[^.]+)/)?.[1] || null;

        if (!topicIdPrefix) {
            transformTopics.value = [];
            transformTopicsTotal.value = 0;
            return;
        }

        transformTopicsPage.value = _.isNil(pageNumber) ? transformTopicsPage.value : pageNumber;
        transformTopicsPageSize.value = _.isNil(newPageSize) ? transformTopicsPageSize.value : newPageSize;
        const { result, total } = await fetchTopicsApi({
            page_size: transformTopicsPageSize.value,
            page_number: transformTopicsPage.value,
            partial_name: topicIdPrefix
        });

        transformTopics.value = result || [];
        transformTopicsTotal.value = total;

        // fetch and attach metrics per topic
        const grouped = _.groupBy(transformTopics.value, 'entity.entity_id');
        const payload = _.map(grouped, (topicsPerEntity, entity_id) => ({
            id: entity_id,
            entity_type: topicsPerEntity[0]?.entity?.entity_type || 'sources',
            connector: topicsPerEntity[0]?.entity?.connector,
            topic_ids: topicsPerEntity[0]?.entity?.topic_ids,
            topic_db_ids: topicsPerEntity[0]?.entity?.topic_db_ids
        }));

        const metricsResponse = await fetchMetricsForTopicsTable(payload);

        transformTopics.value = transformTopics.value.map((topic) => ({
            ...topic,
            metrics: {
                ...topic.metrics,
                ...(metricsResponse[topic.id]?.metrics || {})
            }
        }));
    } finally {
        transformTopicsLoading.value = false;
        transformTopicsMetricsLoading.value = false;
    }
}

// formatting helper for topic metrics
const getTopicFormattedValue = (metricKey, value) => {
    const metricInfo = getMetricFormattingInfo(metricKey, topicsStore.configs?.metrics);
    return formatter(value, metricInfo || {});
};

function goToTopic(topic) {
    router.push({ name: 'topic-details', params: { topic_id: topic.id } });
}

const onTransformTopicsPageSizeChange = async (newPageSize) => {
    try {
        transformTopicsPage.value = 0;
        await fetchTransformTopics(0, newPageSize);
    } catch (error) {
        console.error('Transform details error:', error);
    }
};

onMounted(async () => {
    generalLoading.value = true;
    try {
        await Promise.all([
            transformsStore.getById(route.params.id).then((data) => {
                transform.value = data;
                defaultTransform.value = _.cloneDeep(data);
            }),
            transformsStore.fetchTransformConfigs().then(() => {
                configurationLoading.value = false;
            })
        ]);
    } finally {
        generalLoading.value = false;
    }

    // Tab initialization handled by useUrlFilters and watcher

    onChartChange();
    // load topics configs and fetch the topics table for this transform
    await Promise.all([topicsStore.fetchTopicConfigs(), fetchTransformTopics()]);
});

const onChartChange = () => {
    const option = _.cloneDeep(chartVariants.value).find((x) => x.id === selectedChart.value);
    const chartRef = chart.value?.chart?.chart;

    const options = {
        chart: {
            type: 'column',
            height: '300px'
        },
        title: {
            text: option.chartName
        },
        xAxis: {
            categories: [
                'Jan',
                'Feb',
                'Mar',
                'Apr',
                'May',
                'Jun',
                'Jul',
                'Aug',
                'Sep',
                'Oct',
                'Nov',
                'Dec',
                'Jan',
                'Feb',
                'Mar',
                'Apr',
                'May',
                'Jun',
                'Jul',
                'Aug',
                'Sep',
                'Oct',
                'Nov',
                'Dec'
            ]
        },
        yAxis: {
            min: 0,
            title: {
                text: 'Throughput'
            }
        },
        tooltip: {
            headerFormat: '<span style="font-size:10px">{point.key}</span><table>',
            pointFormat: '<tr><td style="padding:0"><b>{point.y:.1f} units</b></td></tr>',
            footerFormat: '</table>',
            shared: true,
            useHTML: true
        },
        plotOptions: {
            column: {
                pointPadding: 0.2,
                borderWidth: 0,
                dataLabels: {
                    enabled: true
                }
            }
        },
        series: [
            {
                name: `${option.metricName} (${option.unit})`,
                data: option.data, // Mock data
                color: option.color,
                borderRadiusTopLeft: '15%',
                borderRadiusTopRight: '15%'
            }
        ]
    };

    if (chartRef) {
        chartOptions.value = options;
        chartRef.update(options);
    } else {
        chartOptions.value = options;
    }
};

const onSave = async () => {
    try {
        actionLoading.value = true;

        transform.value.transform = transform.value.transform_type;

        // Inject default config values for unchanged properties
        if (transformConfig.value) {
            transform.value.config = transform.value.config || {};
            transformConfig.value.forEach((control) => {
                const name = control.name;
                const defaultVal = control.value?.default;
                if (defaultVal !== undefined && transform.value.config[name] === undefined) {
                    transform.value.config[name] = defaultVal;
                }
            });
        }
        await transformsStore.editTransform(transform.value);

        transform.value = transformsStore.all.find((x) => x.id === transform.value?.id);
        defaultTransform.value = _.cloneDeep(transform.value);
        canSave.value = false;
    } catch (error) {
        console.error('Transform details error:', error);
    } finally {
        actionLoading.value = false;
    }
};

function onDiscard() {
    transform.value = _.cloneDeep(defaultTransform.value);
    canSave.value = false;
}

/**
 * Copy transform ID to clipboard
 */
async function onCopyTransformId(transformId) {
    try {
        await navigator.clipboard.writeText(transformId);
        // Show success toast
        toast.success('Copied!', {
            description: `Transform ID "${transformId}" copied to clipboard`
        });
    } catch (error) {
        console.error('Failed to copy transform ID:', error);
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = transformId;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);

        toast.success('Copied!', {
            description: `Transform ID "${transformId}" copied to clipboard`
        });
    }
}

/**
 * Copy topic ID to clipboard (for topics table)
 */
async function onCopyTopicId(topicId) {
    try {
        await navigator.clipboard.writeText(topicId);
        // Show success toast
        toast.success('Copied!', {
            description: `Topic ID "${topicId}" copied to clipboard`
        });
    } catch (error) {
        console.error('Failed to copy topic ID:', error);
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = topicId;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);

        toast.success('Copied!', {
            description: `Topic ID "${topicId}" copied to clipboard`
        });
    }
}

async function onDelete(name, id) {
    tabLoading.value = true;
    actionLoading.value = true;
    hidePromptTemporary.value = true;

    try {
        await useDeleteConfirmation({
            entity: 'transform',
            name,
            title: 'Delete Transform',
            message: `Are you sure you want to delete the transform <strong>"${name}"</strong>? This action cannot be undone and you will be redirected to the transforms page.`,
            onConfirm: async () => {
                await transformsStore.delete(id);
                // Trigger refresh of transforms list for when user lands on transforms page
                transformsStore.fetch({}, true);
                router.push({ name: 'transforms' });
            }
        });
    } catch (error) {
        // User cancelled or error occurred
        if (error.message) {
            console.error('Delete failed:', error);
        }
    } finally {
        tabLoading.value = false;
        actionLoading.value = false;
        hidePromptTemporary.value = false;
    }
}

async function onClone() {
    try {
        actionLoading.value = true;
        const clonedTransform = await transformsStore.clone(transform.value.id);

        // Navigate to the new cloned transform
        router.push({ name: 'transform-details', params: { id: clonedTransform.id } });

        toast.success(`Transform "${transform.value.name}" successfully cloned.`);
    } catch (error) {
        console.error('Clone failed:', error);
        toast.error('Failed to clone transform.');
    } finally {
        actionLoading.value = false;
    }
}

function onEditImplementation() {
    router.push({ name: 'transform-implementation', params: { id: transform.value.id } });
}

onBeforeRouteLeave((to, from, next) => {
    if (generalLoading.value || actionLoading.value || metricsLoading.value) {
        hideTabs.value = false;
        // Ensure sidebar is expanded when leaving
        if (isMobile.value) {
            setOpenMobile(true);
        } else {
            setOpen(true);
        }
        return next();
    }

    if (hidePromptTemporary.value || !!transform.value?.name) {
        hideTabs.value = false;
        // Ensure sidebar is expanded when leaving
        if (isMobile.value) {
            setOpenMobile(true);
        } else {
            setOpen(true);
        }
        return next();
    }

    const answer = window.confirm('Are you sure you want to leave? You have unsaved changes.');

    if (answer) {
        hideTabs.value = false;
        // Ensure sidebar is expanded when leaving
        if (isMobile.value) {
            setOpenMobile(true);
        } else {
            setOpen(true);
        }
        return next();
    } else {
        return next(false);
    }
});
</script>

<template>
    <!-- PageBreadcrumb teleports itself to App.vue header -->
    <PageBreadcrumb :class="hideTabs ? 'absolute left-10 top-2 right-10 w-auto' : ''">
        <template #breadcrumb-label="{ item }">
            <div v-if="item.name === 'transform-details' && transform" class="flex items-center gap-2">
                <Avatar v-if="transform?.transform_type" class="h-5 w-7 bg-background dark:bg-slate-200 border border-border rounded-md shrink-0">
                    <AvatarImage :src="getTransformIcon(transform.transform_type)" alt="transform icon" class="object-contain p-0.5" />
                    <AvatarFallback class="bg-background dark:bg-slate-200 dark:text-slate-800 text-muted-foreground text-xs rounded-md">
                        {{ transform?.transform_type?.charAt(0).toUpperCase() }}
                    </AvatarFallback>
                </Avatar>
                <div class="flex flex-col">
                    <router-link
                        v-if="transform?.id && hideTabs"
                        :to="`/transforms/view/${transform?.id}`"
                        class="text-accent-foreground hover:text-primary font-medium truncate"
                        style="max-width: 25rem"
                        :title="transform?.name">
                        {{ transform?.name }}
                    </router-link>
                    <span v-if="!hideTabs" class="font-medium text-foreground truncate" style="max-width: 25rem" :title="transform?.name">
                        {{ transform?.id ? transform?.name : 'Loading...' }}
                    </span>
                </div>
                <template v-if="hideTabs">
                    <ChevronRight class="w-4 h-4 mx-2 text-foreground" />
                    <span class="text-foreground">Implementation</span>
                </template>
            </div>
            <span v-else>{{ item.label }}</span>
        </template>
        <template #actions>
            <ActionMenu
                v-if="!generalLoading"
                variant="menu"
                menu-trigger-size="md"
                :disabled="generalLoading || actionLoading || tabLoading"
                :show-copy-id="true"
                :show-delete="true"
                :show-clone="userHasAccess([$Permissions.WRITE_TRANSFORMS], authStore.user)"
                @copyId="onCopyTransformId(transform.id)"
                @delete="onDelete(transform.name, transform.id)"
                @clone="onClone"
                :loading="actionLoading || tabLoading || tabLoading" />
        </template>
    </PageBreadcrumb>

    <div>
        <AppLoader :force-loading="generalLoading" default-height="60vh" class="min-h-60">
            <div v-if="transform" class="w-full">
                <Tabs
                    :tabs="tabs"
                    :initial-active-tab="currentTab"
                    :loading="tabLoading || generalLoading"
                    :hide-tabs="false"
                    @tab-change="setCurrentTab">
                    <template #Status>
                        <div class="mt-5">
                            <div class="w-full mt-3">
                                <h3 class="font-semibold mb-3">Stats</h3>

                                <div class="mt-3 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                                    <StatCard
                                        v-for="item in stats"
                                        :key="item.id"
                                        :icon-bg-class="item.colorClass"
                                        :info-icon="item.infoIcon"
                                        :errors="item.errors"
                                        :icon="item.icon"
                                        :loading="metricsLoading || configurationLoading"
                                        :title="item.name"
                                        :value="item.stat" />
                                </div>
                            </div>
                            <div class="mx-auto w-full px-4 sm:px-6 lg:px-8 p-5 rounded-lg shadow-md space-y-3 bg-card mt-10">
                                <div class="space-y-8 border-primary/10 pb-12 sm:space-y-0 sm:divide-y sm:divide-border sm:pb-0">
                                    <div class="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                                        <Label for="first-name" class="block sm:pt-1.5 flex items-center">Transform Name</Label>
                                        <div class="mt-2 sm:col-span-2 sm:mt-0">
                                            <span class="text-sm text-muted-foreground">{{ transform.name }}</span>
                                        </div>
                                    </div>

                                    <div class="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                                        <Label for="first-name" class="block sm:pt-1.5 flex items-center">Type</Label>
                                        <div class="mt-2 sm:col-span-2 sm:mt-0">
                                            <div class="flex items-center gap-1 text-sm">
                                                <Avatar
                                                    v-if="transform?.transform_type"
                                                    class="h-5 w-7 bg-background dark:bg-slate-200 border border-border rounded-md shrink-0 mr-1">
                                                    <AvatarImage
                                                        :src="getTransformIcon(transform.transform_type)"
                                                        alt="transform icon"
                                                        class="object-contain p-0.5" />
                                                    <AvatarFallback
                                                        class="bg-background dark:bg-slate-200 dark:text-slate-800 text-muted-foreground text-xs rounded-md">
                                                        {{ transform?.transform_type?.charAt(0).toUpperCase() }}
                                                    </AvatarFallback>
                                                </Avatar>
                                                <span class="text-sm text-muted-foreground">{{ transformTypeDisplayName }}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div class="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                                        <Label for="first-name" class="block sm:pt-1.5 flex items-center">Job</Label>
                                        <div class="mt-2 sm:col-span-2 sm:mt-0">
                                            <span class="text-sm text-muted-foreground">{{ transform.job_name }}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <!-- Topics table under Stats -->
                            <div class="w-full mt-5">
                                <h3 class="font-semibold mb-3">Topics</h3>
                                <div class="mt-5">
                                    <ScrollArea
                                        class="w-full border shadow-md rounded-md"
                                        :class="
                                            (transformTopicsLoading && 10 <= 10) || (!transformTopicsLoading && (transformTopics.length || 0) <= 10)
                                                ? 'h-auto'
                                                : 'h-50-dynamic'
                                        ">
                                        <Table>
                                            <TableHeader class="sticky top-0 bg-background z-10 border-b">
                                                <TableRow class="bg-muted/50">
                                                    <TableHead class="first:rounded-tl-md">Name</TableHead>
                                                    <TableHead>Volume</TableHead>
                                                    <TableHead>Errors</TableHead>
                                                    <TableHead>Written</TableHead>
                                                    <TableHead>Last</TableHead>
                                                    <TableHead class="last:rounded-tr-md"></TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                <!-- Skeleton rows when loading -->
                                                <template v-if="transformTopicsLoading">
                                                    <TableRow v-for="n in 10" :key="`skeleton-${n}`">
                                                        <TableCell><SkeletonLoader preset="table-name" :loading="true" /></TableCell>
                                                        <TableCell><SkeletonLoader preset="table-metric" :loading="true" /></TableCell>
                                                        <TableCell><SkeletonLoader preset="table-number" :loading="true" /></TableCell>
                                                        <TableCell><SkeletonLoader preset="table-number" :loading="true" /></TableCell>
                                                        <TableCell><SkeletonLoader preset="table-status" :loading="true" /></TableCell>
                                                        <TableCell><SkeletonLoader preset="table-action" :loading="true" /></TableCell>
                                                    </TableRow>
                                                </template>
                                                <!-- Actual data rows -->
                                                <template v-else>
                                                    <TableRow v-for="topic in transformTopics" :key="topic.id">
                                                        <TableCell class="font-medium">
                                                            <Button
                                                                variant="link"
                                                                class="h-auto p-0 max-w-full text-left justify-start font-medium"
                                                                @click="goToTopic(topic)">
                                                                <span class="hover:text-primary truncate">{{ topic.name }}</span>
                                                            </Button>
                                                        </TableCell>
                                                        <TableCell
                                                            :variant="
                                                                transformTopicsMetricsLoading ||
                                                                getTopicFormattedValue('byteTotal', topic.metrics.byteTotal).display === '-'
                                                                    ? 'default'
                                                                    : 'metric-badge'
                                                            ">
                                                            <SkeletonLoader
                                                                v-if="transformTopicsMetricsLoading"
                                                                preset="inline-metric"
                                                                :loading="true" />
                                                            <template v-else>
                                                                {{ getTopicFormattedValue('byteTotal', topic.metrics.byteTotal).display }}
                                                            </template>
                                                        </TableCell>
                                                        <TableCell
                                                            :variant="
                                                                transformTopicsMetricsLoading ||
                                                                getTopicFormattedValue('recordErrorTotal', topic.metrics.recordErrorTotal).display ===
                                                                    '-'
                                                                    ? 'default'
                                                                    : 'metric-badge'
                                                            ">
                                                            <SkeletonLoader
                                                                v-if="transformTopicsMetricsLoading"
                                                                preset="inline-number"
                                                                :loading="true" />
                                                            <template v-else>
                                                                {{
                                                                    getTopicFormattedValue('recordErrorTotal', topic.metrics.recordErrorTotal).display
                                                                }}
                                                            </template>
                                                        </TableCell>
                                                        <TableCell
                                                            :variant="
                                                                transformTopicsMetricsLoading ||
                                                                getTopicFormattedValue('recordWrittenTotal', topic.metrics.recordWrittenTotal)
                                                                    .display === '-'
                                                                    ? 'default'
                                                                    : 'metric-badge'
                                                            ">
                                                            <SkeletonLoader
                                                                v-if="transformTopicsMetricsLoading"
                                                                preset="inline-number"
                                                                :loading="true" />
                                                            <template v-else>
                                                                {{
                                                                    getTopicFormattedValue('recordWrittenTotal', topic.metrics.recordWrittenTotal)
                                                                        .display
                                                                }}
                                                            </template>
                                                        </TableCell>
                                                        <TableCell
                                                            :variant="
                                                                transformTopicsMetricsLoading ||
                                                                getTopicFormattedValue('StreamingLastEvent', topic.metrics.StreamingLastEvent)
                                                                    .display === '-'
                                                                    ? 'default'
                                                                    : 'metric-badge'
                                                            ">
                                                            <SkeletonLoader
                                                                v-if="transformTopicsMetricsLoading"
                                                                preset="inline-metric"
                                                                :loading="true" />
                                                            <template v-else>
                                                                {{
                                                                    getTopicFormattedValue('StreamingLastEvent', topic.metrics.StreamingLastEvent)
                                                                        .display
                                                                }}
                                                            </template>
                                                        </TableCell>
                                                        <TableCell>
                                                            <ActionMenu
                                                                variant="menu"
                                                                :show-copy-id="true"
                                                                :loading="transformTopicsMetricsLoading"
                                                                @copyId="onCopyTopicId(topic.id)" />
                                                        </TableCell>
                                                    </TableRow>
                                                    <TableEmpty v-if="!transformTopicsLoading && !transformTopics.length" :colspan="6">
                                                        <TableEmptyState />
                                                    </TableEmpty>
                                                </template>
                                            </TableBody>
                                        </Table>
                                        <ScrollBar orientation="horizontal" />
                                        <ScrollBar orientation="vertical" />
                                    </ScrollArea>
                                    <TablePagination
                                        v-if="transformTopics.length > 0 || transformTopicsPage > 0"
                                        :current-page="transformTopicsPage"
                                        :page-size="transformTopicsPageSize"
                                        :loading="transformTopicsLoading"
                                        :current-page-items-count="transformTopics.length"
                                        :total-items="transformTopicsTotal"
                                        item-type="topics"
                                        @page-change="fetchTransformTopics"
                                        @page-size-change="onTransformTopicsPageSizeChange" />
                                </div>
                            </div>
                            <!-- <div class="bg-white p-4 rounded-lg shadow my-5">
                                    <div class="flex items-center justify-between">
                                        <div class="flex gap-2 items-center w-full">
                                            <ChevronUpIcon
                                                v-if="showChart"
                                                class="h-5 w-5 text-muted-foreground cursor-pointer hover:text-foreground"
                                                @click="showChart = !showChart" />
                                            <ChevronDownIcon
                                                v-else
                                                class="h-5 w-5 text-muted-foreground cursor-pointer hover:text-foreground"
                                                @click="showChart = !showChart" />
                                            <h3 v-if="!showChart">{{ chartOptions.title.text }}</h3>
                                        </div>
                                        <div class="w-1/3">
                                            <div class="relative">
                                                <select v-model="selectedChart" class="form-select text-sm" @change="onChartChange">
                                                    <option value="" disabled>Select chart</option>
                                                    <option v-for="variant in chartVariants" :key="variant.id" :value="variant.id">
                                                        {{ variant.metricName }}
                                                    </option>
                                                </select>
                                                <div class="select-icon">
                                                    <ChevronDownIcon class="h-5 w-5 text-muted-foreground" />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <HighChart v-if="showChart" ref="chart" :chart-options="chartOptions" />
                                </div> -->
                        </div>
                    </template>

                    <template #Settings>
                        <div class="mt-5 w-full">
                            <FormSkeleton v-if="configurationLoading" :field-count="5" />
                            <template v-else>
                                <ConnectorTab
                                    v-if="transform.config && transformConfig"
                                    :key="transform.config"
                                    :connector="transform"
                                    :default-connector="defaultTransform"
                                    :config="transformConfig"
                                    :justify-content="'start'"
                                    :entity-type="'transform'"
                                    :tab="'settings'"
                                    :edit-mode="true"
                                    @changed="(status) => (canSave = status)" />
                                <ErrorMessage v-if="formError" :error="formError" :to-sentry="true" :context="'edit-destination'" />
                            </template>
                        </div>
                    </template>

                    <template #Implementation>
                        <div v-if="!transform">Loading transform data...</div>
                        <TransformImplementationPage v-else :transform="transform" />
                    </template>
                </Tabs>
            </div>
        </AppLoader>

        <!-- Footer will teleport to App.vue footer slot -->
        <AppFooter v-if="transform && !hideTabs">
            <template #right>
                <ActionMenu
                    v-if="!generalLoading"
                    variant="row"
                    :show-save="true"
                    :disable-save="!canSave"
                    :show-discard="false"
                    :loading="actionLoading || tabLoading || generalLoading"
                    :disabled="generalLoading || actionLoading"
                    @save="onSave()"
                    @discard="onDiscard" />
            </template>
        </AppFooter>
    </div>
</template>

<style></style>
